/**
 * PostHog Service
 *
 * Thin, defensive wrapper around posthog-js. All feature code goes through
 * `trackingService` which fans out here; no component imports posthog-js
 * directly.
 *
 * Initialization config is set for "maximum capture" per spec:
 *   - Autocapture (clicks, pageviews, pageleave, forms)
 *   - Session recording with strict masking (inputs masked by default)
 *   - Rageclicks, heatmaps
 *   - Exception autocapture
 *   - Performance / web vitals
 *   - Console log recording
 *
 * Privacy guards:
 *   - Inputs masked by default (maskAllInputs: true, passwords always)
 *   - Network request/response bodies and headers are NEVER recorded
 *   - person_profiles: "identified_only" so anonymous traffic does not
 *     balloon our MAU quota unexpectedly
 *
 * Every method is a no-op if initialization failed or is pending, so
 * unconfigured environments (local dev without a key) do not throw.
 */

import posthog, { type BeforeSendFn } from "posthog-js";
import configService from "./configService";
import type { AppBootProfile } from "../utils/adminCustomerViewBoot";
import {
  sanitizeAdminCustomerViewTelemetryValue,
  trySanitizeAdminCustomerViewTelemetryValue,
} from "../utils/adminCustomerViewTelemetry";

export const sanitizeCustomerViewTelemetry: BeforeSendFn = event => {
  if (!event) return null;
  try {
    const next = {
      ...event,
      properties: event.properties
        ? sanitizeAdminCustomerViewTelemetryValue(event.properties)
        : event.properties,
      $set: event.$set
        ? sanitizeAdminCustomerViewTelemetryValue(event.$set)
        : event.$set,
      $set_once: event.$set_once
        ? sanitizeAdminCustomerViewTelemetryValue(event.$set_once)
        : event.$set_once,
    };
    return next;
  } catch {
    return null;
  }
};

function maskCapturedNetworkRequest<T extends { name?: string }>(request: T): T | null {
  if (typeof request.name !== "string") {
    return { ...request };
  }
  const name = trySanitizeAdminCustomerViewTelemetryValue(request.name);
  if (name === null) return null;
  return { ...request, name };
}

function ordinaryInitConfig(isDev: boolean) {
  return {
    autocapture: true as const,
    capture_pageview: "history_change" as const,
    capture_pageleave: "if_capture_pageview" as const,
    capture_performance: true,
    capture_exceptions: true,
    rageclick: true,
    capture_dead_clicks: true,
    enable_heatmaps: true,
    enable_recording_console_log: true,
    disable_session_recording: false,
    debug: isDev,
  };
}

function customerViewInitConfig() {
  return {
    autocapture: false as const,
    capture_pageview: false as const,
    capture_pageleave: false as const,
    capture_performance: false,
    capture_exceptions: false,
    rageclick: false,
    capture_dead_clicks: false,
    enable_heatmaps: false,
    enable_recording_console_log: false,
    disable_session_recording: true,
    disable_surveys: true,
    disable_surveys_automatic_display: true,
    disable_product_tours: true,
    disable_conversations: true,
    disable_external_dependency_loading: true,
    debug: false,
  };
}

class PostHogService {
  private initialized = false;

  initialize(profile: AppBootProfile = "ordinary"): void {
    if (this.initialized || typeof window === "undefined") return;

    const key = configService.getPostHogProjectKey();
    // Ingestion + static assets flow through the managed reverse proxy at
    // d.runplutus.com (first-party domain, dodges ad blockers). Overridable
    // via REACT_APP_POSTHOG_HOST; the fallback IS the proxy so production
    // uses it even if the env var is unset.
    const host = configService.getPostHogHost() || "https://d.runplutus.com";

    if (!key) {
      console.warn("[PostHog] project key not configured; telemetry disabled");
      return;
    }

    const isDev = configService.isLocal() || configService.isDevelopment();
    const privacy = profile === "customer-view"
      ? customerViewInitConfig()
      : ordinaryInitConfig(isDev);

    try {
      posthog.init(key, {
        api_host: host,
        // Required when api_host is a reverse proxy: makes PostHog-generated
        // links (session replay URLs, toolbar) resolve back to the real app.
        ui_host: "https://us.posthog.com",
        person_profiles: "identified_only",
        ...privacy,
        session_recording: {
          maskAllInputs: true,
          maskInputOptions: {
            password: true,
          },
          maskTextClass: "ph-mask",
          recordCrossOriginIframes: false,
          recordHeaders: false,
          recordBody: false,
          maskCapturedNetworkRequestFn: maskCapturedNetworkRequest,
        },
        before_send: sanitizeCustomerViewTelemetry,
        loaded: () => {
          this.initialized = true;
        },
      });
      this.initialized = true;
    } catch (e) {
      console.error("[PostHog] init failed", e);
    }
  }

  identify(userId: string, traits: Record<string, unknown> = {}): void {
    if (!this.initialized) return;
    try {
      posthog.identify(userId, traits);
    } catch (e) {
      console.warn("[PostHog] identify failed", e);
    }
  }

  capture(event: string, props: Record<string, unknown> = {}): void {
    if (!this.initialized) return;
    try {
      posthog.capture(event, props);
    } catch (e) {
      console.warn("[PostHog] capture failed", e);
    }
  }

  /**
   * Register session-scoped super properties. PostHog merges these into
   * every subsequent `capture()` call until either `unregisterForSession()`
   * is called for the key or the current browser session ends. Used by
   * surface-scoped cohort tagging (e.g. `page_version = v2` during the
   * AuthPageV2 mount), where we want every event that fires while the
   * user is on the surface -- including events emitted from shared
   * services like `analyticsService` and `AuthContext`, which do not
   * accept a per-call metadata bag -- to carry the cohort fields.
   *
   * Session scope (rather than the persistent `register()`) caps
   * worst-case leakage at the current session if the caller's cleanup
   * never runs (e.g. tab crash). Callers are still expected to call
   * `unregisterForSession()` on unmount to prevent within-session leakage
   * into post-surface events.
   *
   * The session register also holds the campaign params posthog-js saves
   * from the landing URL (`utm_*`, ...). Registering one of those keys
   * replaces the visitor's value, so a caller that does should read it first
   * (`getSessionProperty`) and put it back on cleanup.
   */
  registerForSession(props: Record<string, unknown>): void {
    if (!this.initialized) return;
    try {
      posthog.register_for_session(props);
    } catch (e) {
      console.warn("[PostHog] register_for_session failed", e);
    }
  }

  /**
   * Remove a super property from the persistent register only. It does NOT
   * clear properties set with `registerForSession`, which live in the session
   * register: use `unregisterForSession` to undo those.
   */
  unregister(propertyName: string): void {
    if (!this.initialized) return;
    try {
      posthog.unregister(propertyName);
    } catch (e) {
      console.warn("[PostHog] unregister failed", e);
    }
  }

  /**
   * Remove a session super property, touching the session register only.
   * Undoes `registerForSession`. It never reaches the persistent register, so
   * it cannot delete a persistent property of the same name that the caller
   * did not set. Landing-URL `utm_*` live in the session register, so a
   * caller that registered over them must restore them, not just remove them.
   */
  unregisterForSession(propertyName: string): void {
    if (!this.initialized) return;
    try {
      posthog.unregister_for_session(propertyName);
    } catch (e) {
      console.warn("[PostHog] unregister_for_session failed", e);
    }
  }

  /**
   * Read a session super property: one set with `registerForSession`, or one
   * posthog-js saved from the landing URL (`utm_*`, `gclid`, ...). `undefined`
   * when it is not set, PostHog is not ready, or the read fails.
   */
  getSessionProperty(propertyName: string): unknown {
    if (!this.initialized) return undefined;
    try {
      return posthog.getSessionProperty(propertyName);
    } catch {
      return undefined;
    }
  }

  captureException(error: Error, context: Record<string, unknown> = {}): void {
    if (!this.initialized) return;
    try {
      posthog.captureException(error, context);
    } catch (e) {
      console.warn("[PostHog] captureException failed", e);
    }
  }

  setAlphaPreviewActive(_active: boolean): void {
    // Alpha is a live analytics surface. Replay stays on.
  }

  optOut(): void {
    if (!this.initialized) return;
    try {
      posthog.opt_out_capturing();
    } catch {
      // Never surface telemetry errors.
    }
  }

  optIn(): void {
    if (!this.initialized) return;
    try {
      posthog.opt_in_capturing();
    } catch {
      // Never surface telemetry errors.
    }
  }

  reset(): void {
    if (!this.initialized) return;
    try {
      posthog.reset();
    } catch {
      // Never surface telemetry errors.
    }
  }

  isReady(): boolean {
    return this.initialized;
  }

  getRequestHeaders(): Record<string, string> {
    if (!this.initialized || this.isOptedOut()) return {};

    const identityHeaders: Array<[string, string | null]> = [
      ["X-Posthog-Device-Id", this.getDeviceId()],
      ["X-Posthog-Distinct-Id", this.getDistinctId()],
      ["X-Posthog-Session-Id", this.getSessionId()],
    ];
    const headers: Record<string, string> = {};
    for (const [name, value] of identityHeaders) {
      // Invalid optional metadata must not prevent the API request from being sent.
      if (typeof value === "string" && value.trim() && /^[\x20-\x7e]+$/.test(value)) {
        headers[name] = value;
      }
    }
    return headers;
  }

  readFeatureFlag(key: string, fresh = false): boolean | string | undefined {
    if (!this.initialized || this.isOptedOut()) return undefined;
    try {
      return posthog.getFeatureFlag(key, { send_event: false, fresh });
    } catch {
      return undefined;
    }
  }

  onFeatureFlags(listener: (errorsLoading: boolean) => void): () => void {
    if (!this.initialized || this.isOptedOut()) return () => undefined;
    try {
      return posthog.onFeatureFlags((_flags, _variants, context) => listener(Boolean(context?.errorsLoading)));
    } catch {
      return () => undefined;
    }
  }

  recordFeatureFlagExposure(key: string, variant: string): boolean {
    if (!this.initialized || this.isOptedOut()) return false;
    try {
      // The route supplies its frozen, freshly resolved assignment after paint.
      // Re-reading here could report a newer SDK value than the page actually seen.
      posthog.capture("$experiment_exposure", {
        $feature_flag: key,
        $feature_flag_response: variant,
        [`$feature/${key}`]: variant,
      });
      return true;
    } catch {
      return false;
    }
  }

  isOptedOut(): boolean {
    if (!this.initialized) return false;
    try {
      return posthog.has_opted_out_capturing();
    } catch {
      return false;
    }
  }

  getDistinctId(): string | null {
    if (!this.initialized) return null;
    try {
      return posthog.get_distinct_id() ?? null;
    } catch {
      return null;
    }
  }

  getDeviceId(): string | null {
    if (!this.initialized) return null;
    try {
      if (posthog.has_opted_out_capturing()) return null;
      // identify() changes distinct_id to the user ID; the original browser ID stays here.
      const deviceId = posthog.get_property("$device_id");
      return typeof deviceId === "string" ? deviceId : null;
    } catch {
      return null;
    }
  }

  getSessionId(): string | null {
    if (!this.initialized) return null;
    try {
      return posthog.get_session_id() ?? null;
    } catch {
      return null;
    }
  }
}

export const posthogService = new PostHogService();
export default posthogService;
